import React, { useContext } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { Avatar, theme, TitleDropdown } from 'components';
import { AuthContext } from 'contexts';
import { useBreakpoint } from 'hooks';

import { isTeamAdmin } from '@bb/users';
import ListingActions from './ListingActions';
import type { DashboardType } from '.';
import DisabledAutoCoopBanner from './DisabledAutoCoopBanner';

type MyBusinessPageHeaderProps = {
  dashboardType: DashboardType;
  setDashboardType?: (type: DashboardType) => void;
};

const MyBusinessPageHeader: React.FC<MyBusinessPageHeaderProps> = ({
  dashboardType,
  setDashboardType,
}) => {
  const { auth } = useContext(AuthContext);
  const { md } = useBreakpoint();

  if (!md) return null;

  const getBrokerageLogoSrc = () => {
    const {
      address,
      location: authLocation,
      org: { addresses, logoUrl },
    } = auth;

    const currLocationLogo =
      addresses?.[Number(authLocation)]?.[Number(address)]?.logo;

    return currLocationLogo ?? logoUrl;
  };

  return (
    <HeaderContainer data-cy="dashboard-header">
      <DisabledAutoCoopBanner />
      <TodayDate data-cy="dashboard-date">
        {moment().format('ddd MMM D, YYYY')}
      </TodayDate>
      <AvatarHeaderContainer>
        <Avatar
          user={auth}
          width={24}
          src={dashboardType === 'team' ? getBrokerageLogoSrc() : undefined}
        />
        <MainText data-cy="dashboard-title">
          {isTeamAdmin(auth) ? (
            <TitleDropdown
              type="dashboard"
              mainTitleLevel={2}
              itemTitleLevel={3}
              dashboardType={dashboardType}
              setDashboardType={setDashboardType}
            />
          ) : (
            `${
              dashboardType === 'me' ? `${auth.firstName}'s` : 'Team'
            } Dashboard`
          )}
        </MainText>

        <ListingActions />
      </AvatarHeaderContainer>
    </HeaderContainer>
  );
};

export default MyBusinessPageHeader;

const HeaderContainer = styled.div`
  margin-bottom: 24px;
`;

const TodayDate = styled.div`
  color: ${theme.gray8};
  margin-bottom: 4px;
`;

const MainText = styled.span`
  margin-left: 8px;
  font-weight: 600;
  font-size: 20px;
  line-height: 28px;
`;

const RadioButtonContainer = styled.span`
  flex-grow: 1;
  margin-left: 24px;
`;

const AvatarHeaderContainer = styled.span`
  display: flex;
  align-items: center;
`;
